import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  parseErrorResponse,
  isTerminalState,
  getSummaryIdentity,
  convertFileToBase64,
} from './useSwarmSessionHelpers';
import { isAgentDisabled } from '../utils/userPageHelpers';

const MAX_EVENTS = 300;
const AUTO_SUMMARY_WINDOW_MS = 130000;

export const useSwarmSession = (API_URL, conversationId, modelSettings) => {
  const [sessionId, setSessionId] = useState(null);
  const [swarmState, setSwarmState] = useState(null);
  const [events, setEvents] = useState([]);
  // Live token-delta partials keyed by `${turn_index}:${speaker_id}`.  These
  // are best-effort UX only (never persisted server-side) and are kept OUT of
  // `events`, so live deltas can never evict durable events from the
  // MAX_EVENTS ring; a turn's terminal agent_message clears its partial.
  const [liveDeltas, setLiveDeltas] = useState({});
  const [status, setStatus] = useState(null);
  const [starting, setStarting] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [intervening, setIntervening] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [pauseUpdating, setPauseUpdating] = useState(false);
  const [delayUpdating, setDelayUpdating] = useState(false);
  const [manualSummarizing, setManualSummarizing] = useState(false);
  const [autoSummarizing, setAutoSummarizing] = useState(false);
  const [summaries, setSummaries] = useState([]);
  const [error, setError] = useState(null);

  const streamAbortControllerRef = useRef(null);
  const activeSessionIdRef = useRef(null);
  const isHistoricalLoadRef = useRef(false);
  const summaryInFlightRef = useRef(false);
  const autoSummaryInFlightRef = useRef(false);
  const autoSummaryDeadlineRef = useRef(0);
  const summaryRequestTokenRef = useRef(0);
  // rAF coalescer for liveDeltas: accumulate per-token into the ref, flush one
  // setLiveDeltas per animation frame (mirrors useChatSessions.js).
  const liveDeltaAccumRef = useRef({});
  const deltaRafIdRef = useRef(null);
  const deltaFrameScheduledRef = useRef(false);
  const summarizing = manualSummarizing || autoSummarizing;

  const participantDisplayNames = useMemo(() => {
    const displayNames = {};
    const { agentModelAssignments, personas } = modelSettings || {};

    if (!agentModelAssignments) {
      return {
        aeri: 'Aeri (Socratic Philosopher)',
        capo: 'Capo (Strategic Executive)',
        cardian: 'Cardian (Systems Architect)',
      };
    }

    const personaTitleMap = new Map(
      (personas || []).map(p => [p.id, p.title]).filter(([id, title]) => id && title)
    );

    for (const agentId in agentModelAssignments) {
      const assignment = agentModelAssignments[agentId];
      // Disabled agents are not speakers, so they get no display name.
      if (isAgentDisabled(assignment)) continue;
      const personaId = assignment?.persona_id;
      const agentName = agentId.charAt(0).toUpperCase() + agentId.slice(1);
      const personaTitle = personaTitleMap.get(personaId);

      if (personaTitle) {
        displayNames[agentId] = `${agentName} (${personaTitle})`;
      } else {
        displayNames[agentId] = agentName;
      }
    }

    // Re-add the built-in defaults only when present and not disabled.
    if (!displayNames.aeri && !isAgentDisabled(agentModelAssignments.aeri))
      displayNames.aeri = 'Aeri (Socratic Philosopher)';
    if (!displayNames.capo && !isAgentDisabled(agentModelAssignments.capo))
      displayNames.capo = 'Capo (Strategic Executive)';
    if (!displayNames.cardian && !isAgentDisabled(agentModelAssignments.cardian))
      displayNames.cardian = 'Cardian (Systems Architect)';

    return displayNames;
  }, [modelSettings]);

  const appendEvent = useCallback((event) => {
    setEvents(prev => {
      const next = [...prev, event];
      if (next.length > MAX_EVENTS) {
        return next.slice(next.length - MAX_EVENTS);
      }
      return next;
    });
  }, []);

  // Flush accumulated live deltas into render state (one frame).
  const flushDeltaFrame = useCallback(() => {
    deltaFrameScheduledRef.current = false;
    deltaRafIdRef.current = null;
    setLiveDeltas({ ...liveDeltaAccumRef.current });
  }, []);
  // Schedule one flush per animation frame.  Guard on the boolean (not the
  // id): the synchronous test rAF stub runs the callback before the id is
  // assigned, so an id guard would wedge.
  const scheduleDeltaFlush = useCallback(() => {
    if (deltaFrameScheduledRef.current) return;
    deltaFrameScheduledRef.current = true;
    deltaRafIdRef.current = requestAnimationFrame(flushDeltaFrame);
  }, [flushDeltaFrame]);
  // Cancel any pending frame (the caller then sets state synchronously).
  const cancelDeltaFrame = useCallback(() => {
    deltaFrameScheduledRef.current = false;
    if (deltaRafIdRef.current != null) {
      cancelAnimationFrame(deltaRafIdRef.current);
      deltaRafIdRef.current = null;
    }
  }, []);
  // Drop all live partials (session end / reset).
  const clearLiveDeltas = useCallback(() => {
    cancelDeltaFrame();
    liveDeltaAccumRef.current = {};
    setLiveDeltas({});
  }, [cancelDeltaFrame]);
  // Drop one partial by key and apply synchronously (terminal replace).
  const dropLiveDelta = useCallback((key) => {
    if (liveDeltaAccumRef.current[key] === undefined) return;
    cancelDeltaFrame();
    const { [key]: _drop, ...rest } = liveDeltaAccumRef.current;
    liveDeltaAccumRef.current = rest;
    setLiveDeltas({ ...rest });
  }, [cancelDeltaFrame]);

  const stopStream = useCallback(() => {
    if (streamAbortControllerRef.current) {
      streamAbortControllerRef.current.abort();
      streamAbortControllerRef.current = null;
    }
    setStreaming(false);
  }, []);

  const refreshStatus = useCallback(async (targetSessionId = null) => {
    const sid = targetSessionId || activeSessionIdRef.current;
    if (!sid) return null;

    try {
      const response = await fetch(
        `${API_URL}/swarm/status?session_id=${encodeURIComponent(sid)}`,
        { credentials: 'include' }
      );

      if (!response.ok) {
        const { message } = await parseErrorResponse(response);
        if (sid === activeSessionIdRef.current) setError(message);
        return null;
      }

      const data = await response.json();
      // Guard against stale updates: if the session changed while waiting,
      // discard the result rather than overwriting the new session's state.
      if (sid !== activeSessionIdRef.current) return null;
      setStatus(data);
      setSwarmState(data.state || null);
      return data;
    } catch (e) {
      const message = e?.message || 'Failed to refresh swarm status';
      if (sid === activeSessionIdRef.current) setError(message);
      return null;
    }
  }, [API_URL]);

  const streamSwarm = useCallback(async (targetSessionId, fromSeq = 1) => {
    if (!targetSessionId) return;

    stopStream();

    const controller = new AbortController();
    streamAbortControllerRef.current = controller;
    setStreaming(true);

    // Initial status fetch to populate metadata (like max_turns)
    await refreshStatus(targetSessionId);

    try {
      const response = await fetch(
        `${API_URL}/swarm/stream?session_id=${encodeURIComponent(targetSessionId)}&from_seq=${fromSeq}`,
        {
          method: 'GET',
          credentials: 'include',
          signal: controller.signal,
        }
      );

      if (!response.ok) {
        const { message } = await parseErrorResponse(response);
        setError(message);
        return;
      }

      const reader = response.body?.getReader();
      if (!reader) {
        setError('Swarm stream is not available');
        return;
      }

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.trim()) continue;
          try {
            const event = JSON.parse(line);

            if (event.type === 'agent_message_delta') {
              // Live-only fragment: accumulate into liveDeltas and flush once
              // per frame; NEVER append to `events` (keeps deltas out of the
              // durable ring) — hence the early continue.
              const p = event.payload || {};
              const key = `${p.turn_index}:${p.speaker_id}`;
              const prevText = liveDeltaAccumRef.current[key]?.text || '';
              liveDeltaAccumRef.current = {
                ...liveDeltaAccumRef.current,
                [key]: {
                  turn_index: p.turn_index,
                  speaker_id: p.speaker_id,
                  text: prevText + (p.delta || ''),
                },
              };
              scheduleDeltaFlush();
              continue;
            }

            appendEvent(event);

            // Real-time progress update from event payloads
            const turnIdx = event.payload?.turn_index || event.payload?.applied_at_turn;
            if (turnIdx !== undefined) {
              setStatus(prev => {
                if (!prev) return prev;
                return {
                  ...prev,
                  progress: {
                    ...prev.progress,
                    turns_completed: Math.max(prev.progress?.turns_completed || 0, turnIdx)
                  }
                };
              });
            }

            if (event.type === 'state_changed') {
              const nextState = event?.payload?.to;
              if (nextState) setSwarmState(nextState);
            } else if (event.type === 'agent_message') {
              // The turn's authoritative message landed (now in `events`);
              // drop its live partial so the provisional card is replaced by
              // the durable committed one.
              const p = event.payload || {};
              dropLiveDelta(`${p.turn_index}:${p.speaker_id}`);
            } else if (event.type === 'pause_mode_changed') {
              const nextMode = event?.payload?.to;
              if (nextMode !== undefined) {
                setStatus(prev => prev ? { ...prev, pause_mode: nextMode } : prev);
              }
            } else if (event.type === 'turn_delay_changed') {
              const nextDelay = event?.payload?.to;
              if (nextDelay !== undefined) {
                setStatus(prev => prev ? { ...prev, turn_delay: nextDelay } : prev);
              }
            } else if (event.type === 'session_completed') {
              setSwarmState('completed');
              clearLiveDeltas();
              const hasFinalSummary = summaries.some(s => s?.is_final === true);
              const hasSummaryError = Boolean(status?.completion?.summary_error);
              if (!hasFinalSummary && !hasSummaryError) {
                autoSummaryInFlightRef.current = true;
                autoSummaryDeadlineRef.current = Date.now() + AUTO_SUMMARY_WINDOW_MS;
                setAutoSummarizing(true);
              }
            } else if (event.type === 'session_stopped') {
              setSwarmState('stopped');
              clearLiveDeltas();
              // Stopped sessions should not auto-trigger summary generation.
              autoSummaryInFlightRef.current = false;
              autoSummaryDeadlineRef.current = 0;
              setAutoSummarizing(false);
            } else if (event.type === 'summary_generated') {
              // The durable summary landed; drop any streaming summary
              // partial (key uses turn_index null + speaker "summary").
              dropLiveDelta('null:summary');
              setSummaries(prev => {
                const incoming = event?.payload;
                const incomingIdentity = getSummaryIdentity(incoming);
                if (!incomingIdentity) {
                  return prev;
                }
                if (prev.some(s => getSummaryIdentity(s) === incomingIdentity)) {
                  return prev;
                }
                return [...prev, incoming];
              });

              // If a manual request is in flight, the stream event is
              // authoritative for completion and can end loading immediately.
              if (summaryInFlightRef.current) {
                summaryInFlightRef.current = false;
                setManualSummarizing(false);
              }
              // Keep terminal auto-summary loading active until final summary arrives.
              if (event?.payload?.is_final === true) {
                autoSummaryInFlightRef.current = false;
                autoSummaryDeadlineRef.current = 0;
                setAutoSummarizing(false);
              }
            } else if (event.type === 'warning') {
              const warningCode = event?.payload?.code;
              const warningMsg = event?.payload?.message || 'Swarm warning';
              if (warningCode === 'SWARM_SUMMARY_ERROR') {
                // Summary generation failed — drop any streamed summary
                // partial so the "Summary (streaming…)" preview doesn't stick.
                dropLiveDelta('null:summary');
                autoSummaryInFlightRef.current = false;
                autoSummaryDeadlineRef.current = 0;
                setAutoSummarizing(false);
              }
              setError(warningMsg);
            } else if (event.type === 'error') {
              const msg = event?.payload?.message || 'Swarm stream error';
              setError(msg);
            }
          } catch (e) {
            console.error('Failed to parse swarm event line', e, line);
          }
        }
      }
    } catch (e) {
      if (e?.name !== 'AbortError') {
        setError(e?.message || 'Swarm stream failed');
      }
    } finally {
      if (streamAbortControllerRef.current === controller) {
        streamAbortControllerRef.current = null;
        setStreaming(false);
        await refreshStatus(targetSessionId);
      }
    }
  }, [API_URL, appendEvent, clearLiveDeltas, dropLiveDelta, refreshStatus, scheduleDeltaFlush, status?.completion?.summary_error, stopStream, summaries]);

  useEffect(() => {
    if (!isTerminalState(swarmState)) {
      return;
    }

    if (swarmState !== 'completed') {
      autoSummaryInFlightRef.current = false;
      autoSummaryDeadlineRef.current = 0;
      setAutoSummarizing(false);
      return;
    }

    // If the final summary is already present, the auto-summarize cycle is done.
    // Only short-circuit on a final summary — intermediate summaries from a
    // replayed historical stream must not prevent the spinner from activating
    // when the session subsequently completes in the current browser session.
    const hasFinalSummaryEarly = summaries.some(s => s?.is_final === true);
    if (hasFinalSummaryEarly) {
      autoSummaryInFlightRef.current = false;
      autoSummaryDeadlineRef.current = 0;
      setAutoSummarizing(false);
      return;
    }

    const hasFinalSummary = hasFinalSummaryEarly;
    const hasSummaryError = Boolean(status?.completion?.summary_error);
    const isPending = Boolean(status?.completion?.summary_pending);
    const now = Date.now();

    if (!hasFinalSummary && !hasSummaryError && autoSummaryDeadlineRef.current === 0) {
      autoSummaryDeadlineRef.current = now + AUTO_SUMMARY_WINDOW_MS;
    }

    const withinAutoSummaryWindow = autoSummaryDeadlineRef.current > now;
    const shouldAutoSummarize = (
      !hasFinalSummary
      && !hasSummaryError
      && (isPending || autoSummaryInFlightRef.current || withinAutoSummaryWindow)
    );

    autoSummaryInFlightRef.current = shouldAutoSummarize;
    if (!shouldAutoSummarize) {
      autoSummaryDeadlineRef.current = 0;
    }
    setAutoSummarizing(shouldAutoSummarize);
  }, [status?.completion?.summary_error, status?.completion?.summary_pending, summaries, swarmState]);

  // Safety net: if autoSummarizing is still on when the deadline passes,
  // force-clear it so the button never spins indefinitely.
  useEffect(() => {
    if (!autoSummarizing) return;
    const remaining = Math.max(0, autoSummaryDeadlineRef.current - Date.now());
    const id = setTimeout(() => {
      setAutoSummarizing(false);
      autoSummaryInFlightRef.current = false;
      autoSummaryDeadlineRef.current = 0;
    }, remaining > 0 ? remaining : AUTO_SUMMARY_WINDOW_MS);
    return () => clearTimeout(id);
  }, [autoSummarizing]);

  const pauseSwarm = useCallback(async ({ reason = null } = {}) => {
    if (!sessionId) {
      setError('No active swarm session found.');
      return null;
    }

    setPauseUpdating(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/swarm/pause`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          session_id: sessionId,
          reason,
        }),
      });

      if (!response.ok) {
        const { message } = await parseErrorResponse(response);
        setError(message);
        setPauseUpdating(false);
        return null;
      }

      const data = await response.json();
      setSwarmState(data.state || swarmState);
      await refreshStatus();
      setPauseUpdating(false);
      return data;
    } catch (e) {
      const message = e?.message || 'Failed to pause swarm';
      setError(message);
      setPauseUpdating(false);
      return null;
    }
  }, [API_URL, refreshStatus, sessionId, swarmState]);

  const resumeSwarm = useCallback(async ({ reason = null } = {}) => {
    if (!sessionId) {
      setError('No active swarm session found.');
      return null;
    }

    setPauseUpdating(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/swarm/resume`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          session_id: sessionId,
          reason,
        }),
      });

      if (!response.ok) {
        const { message } = await parseErrorResponse(response);
        setError(message);
        setPauseUpdating(false);
        return null;
      }

      const data = await response.json();
      setSwarmState(data.state || swarmState);
      await refreshStatus();
      setPauseUpdating(false);
      return data;
    } catch (e) {
      const message = e?.message || 'Failed to resume swarm';
      setError(message);
      setPauseUpdating(false);
      return null;
    }
  }, [API_URL, refreshStatus, sessionId, swarmState]);

  const setPauseMode = useCallback(async (mode) => {
    if (!sessionId) {
      setError('No active swarm session found.');
      return null;
    }

    setPauseUpdating(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/swarm/pause-mode`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          session_id: sessionId,
          mode,
        }),
      });

      if (!response.ok) {
        const { message } = await parseErrorResponse(response);
        setError(message);
        setPauseUpdating(false);
        return null;
      }

      const data = await response.json();
      setSwarmState(data.state || swarmState);
      setStatus(prev => prev ? { ...prev, pause_mode: data.pause_mode } : prev);
      await refreshStatus();
      setPauseUpdating(false);
      return data;
    } catch (e) {
      const message = e?.message || 'Failed to update pause mode';
      setError(message);
      setPauseUpdating(false);
      return null;
    }
  }, [API_URL, refreshStatus, sessionId, swarmState]);

  const setTurnDelay = useCallback(async (delaySec) => {
    if (!sessionId) {
      setError('No active swarm session found.');
      return null;
    }

    setDelayUpdating(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/swarm/turn-delay`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          session_id: sessionId,
          delay_sec: parseFloat(delaySec),
        }),
      });

      if (!response.ok) {
        const { message } = await parseErrorResponse(response);
        setError(message);
        setDelayUpdating(false);
        return null;
      }

      const data = await response.json();
      setStatus(prev => prev ? { ...prev, turn_delay: data.delay_sec } : prev);
      await refreshStatus();
      setDelayUpdating(false);
      return data;
    } catch (e) {
      const message = e?.message || 'Failed to update turn delay';
      setError(message);
      setDelayUpdating(false);
      return null;
    }
  }, [API_URL, refreshStatus, sessionId]);

  const startSwarm = useCallback(async ({
    question,
    files = [],
    maxTurns = 12,
    researchPolicy = null,
    priorContext = null,
  }) => {
    if (!question?.trim() && (!files || files.length === 0)) {
      setError('Please enter a question or attach at least one file.');
      return null;
    }

    // A swarm is a debate: it needs at least two active (non-disabled)
    // agents. Ask mode covers the single-agent case.
    const activeAgents = Object.values(
      modelSettings?.agentModelAssignments || {}
    ).filter((a) => !isAgentDisabled(a));
    if (activeAgents.length < 2) {
      setError(
        'Swarm needs at least 2 active agents to debate. Enable another '
        + 'agent (set its provider) or use Ask mode for a single agent.'
      );
      return null;
    }

    setStarting(true);
    setError(null);
    setEvents([]);
    clearLiveDeltas();
    setStatus(null);
    setSessionId(null);
    activeSessionIdRef.current = null;
    setSwarmState(null);
    setSummaries([]);
    setManualSummarizing(false);
    setAutoSummarizing(false);
    summaryInFlightRef.current = false;
    autoSummaryInFlightRef.current = false;
    autoSummaryDeadlineRef.current = 0;
    summaryRequestTokenRef.current += 1;

    try {
      const processedFiles = await Promise.all(
        (files || []).map(file => convertFileToBase64(file))
      );

      const body = {
        conversation_id: conversationId,
        question,
        provider: modelSettings.provider,
        model: modelSettings.model,
        agent_models: modelSettings.agentModelAssignments,
        files: processedFiles.length > 0 ? processedFiles : null,
        turn_policy: {
          max_turns: parseInt(maxTurns, 10) ?? 12
        },
        prior_context: priorContext && priorContext.length > 0 ? priorContext : null,
        research_policy: {
          grace_window_sec: Number.isFinite(Number(researchPolicy?.grace_window_sec))
            ? Number(researchPolicy.grace_window_sec)
            : 20,
          tool_timeout_sec: Number.isFinite(Number(researchPolicy?.tool_timeout_sec))
            ? Number(researchPolicy.tool_timeout_sec)
            : 120,
          inject_if_terminal: Boolean(researchPolicy?.inject_if_terminal ?? false),
        },
      };

      const response = await fetch(`${API_URL}/swarm/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const { message } = await parseErrorResponse(response);
        setError(message);
        setStarting(false);
        return null;
      }

      const data = await response.json();
      setSessionId(data.session_id);
      activeSessionIdRef.current = data.session_id;
      setSwarmState(data.state || 'running');

      setStarting(false);
      streamSwarm(data.session_id, 1);
      
      return data;
    } catch (e) {
      const message = e?.message || 'Failed to start swarm session';
      setError(message);
      setStarting(false);
      return null;
    }
  }, [API_URL, clearLiveDeltas, conversationId, modelSettings.agentModelAssignments, modelSettings.model, modelSettings.provider, streamSwarm]);

  const intervene = useCallback(async ({ content, files = [] }) => {
    if (!sessionId) {
      setError('No active swarm session found.');
      return null;
    }

    if (!content?.trim() && (!files || files.length === 0)) {
      setError('Intervention requires content or file(s).');
      return null;
    }

    setIntervening(true);
    setError(null);

    try {
      const processedFiles = await Promise.all(
        (files || []).map(file => convertFileToBase64(file))
      );

      const response = await fetch(`${API_URL}/swarm/intervene`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          session_id: sessionId,
          content: content || null,
          files: processedFiles.length > 0 ? processedFiles : null,
        }),
      });

      if (!response.ok) {
        const { message } = await parseErrorResponse(response);
        setError(message);
        setIntervening(false);
        return null;
      }

      const data = await response.json();
      await refreshStatus();
      setIntervening(false);
      return data;
    } catch (e) {
      const message = e?.message || 'Failed to queue intervention';
      setError(message);
      setIntervening(false);
      return null;
    }
  }, [API_URL, refreshStatus, sessionId]);

  const stopSwarm = useCallback(async ({ force = false, reason = null } = {}) => {
    if (!sessionId) {
      setError('No active swarm session found.');
      return null;
    }

    setStopping(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/swarm/stop`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          session_id: sessionId,
          reason,
          force,
        }),
      });

      if (!response.ok) {
        const { message, code } = await parseErrorResponse(response);

        // A completed/stopped session is terminal; treat late stop clicks as no-op.
        if (response.status === 409 && code === 'SWARM_ALREADY_STOPPED') {
          const latest = await refreshStatus();
          setSwarmState(latest?.state || swarmState);
          setStopping(false);
          return {
            status: 'accepted',
            session_id: sessionId,
            state: latest?.state || swarmState,
            effective_mode: force ? 'forced' : 'graceful',
            requested_at: new Date().toISOString(),
          };
        }

        setError(message);
        setStopping(false);
        return null;
      }

      const data = await response.json();
      setSwarmState(data.state || swarmState);

      if (force) {
        stopStream();
      }

      await refreshStatus();
      setStopping(false);
      return data;
    } catch (e) {
      const message = e?.message || 'Failed to stop swarm';
      setError(message);
      setStopping(false);
      return null;
    }
  }, [API_URL, refreshStatus, sessionId, stopStream, swarmState]);

  const getSummary = useCallback(async () => {
    if (!sessionId) {
      setError('No active swarm session found.');
      return null;
    }

    if (summaryInFlightRef.current || summarizing) {
      return null;
    }

    const requestSessionId = sessionId;
    const requestToken = summaryRequestTokenRef.current + 1;
    summaryRequestTokenRef.current = requestToken;
    summaryInFlightRef.current = true;
    setManualSummarizing(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/swarm/summary`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          session_id: requestSessionId,
          requested_turn: status?.progress?.turns_completed ?? null,
        }),
      });

      if (!response.ok) {
        const { message, code } = await parseErrorResponse(response);

        // Generation needs the live in-memory session; after a swarm
        // restart only a previously stored summary can be fetched. The
        // generic not-found would invite retries that can never succeed.
        if (response.status === 404 && code === 'SWARM_SESSION_NOT_FOUND') {
          setError(
            'This session is no longer on the server, so a summary can no '
            + 'longer be generated for it. You can start a new swarm from '
            + 'this topic instead.'
          );
          return null;
        }

        setError(message);
        return null;
      }

      const data = await response.json();

      // Ignore late completion from stale requests when a newer summary
      // request has already started.
      if (summaryRequestTokenRef.current !== requestToken) {
        return null;
      }

      setSummaries(prev => {
        const incomingIdentity = getSummaryIdentity(data);
        if (!incomingIdentity) {
          return prev;
        }
        if (prev.some(s => getSummaryIdentity(s) === incomingIdentity)) {
          return prev;
        }
        return [...prev, data];
      });
      // A prior stream abort can strand a mid-stream 'null:summary'
      // partial, which would suppress the durable summary card — the
      // fetched summary supersedes any live preview.
      dropLiveDelta('null:summary');
      return data;
    } catch (e) {
      const message = e?.message || 'Failed to get summary';
      setError(message);
      return null;
    } finally {
      if (summaryRequestTokenRef.current === requestToken) {
        setManualSummarizing(false);
        summaryInFlightRef.current = false;
      }
    }
  }, [API_URL, dropLiveDelta, sessionId, status, summarizing]);

  const loadSession = useCallback((targetSessionId) => {
    if (!targetSessionId) return;

    stopStream();
    isHistoricalLoadRef.current = true;
    setSessionId(targetSessionId);
    activeSessionIdRef.current = targetSessionId;
    setSwarmState(null);
    setEvents([]);
    clearLiveDeltas();
    setStatus(null);
    setSummaries([]);
    setError(null);
    setStarting(false);
    setStreaming(false);
    setManualSummarizing(false);
    setAutoSummarizing(false);
    summaryInFlightRef.current = false;
    autoSummaryInFlightRef.current = false;
    autoSummaryDeadlineRef.current = 0;
    summaryRequestTokenRef.current += 1;

    // streamSwarm clears isHistoricalLoadRef in its finally block
    streamSwarm(targetSessionId, 1).finally(() => {
      isHistoricalLoadRef.current = false;
    });
  }, [clearLiveDeltas, stopStream, streamSwarm]);

  const resetSwarm = useCallback(() => {
    stopStream();
    isHistoricalLoadRef.current = false;
    setSessionId(null);
    activeSessionIdRef.current = null;
    setSwarmState(null);
    setEvents([]);
    clearLiveDeltas();
    setStatus(null);
    setStarting(false);
    setStreaming(false);
    setIntervening(false);
    setStopping(false);
    setPauseUpdating(false);
    setDelayUpdating(false);
    setManualSummarizing(false);
    setAutoSummarizing(false);
    setSummaries([]);
    setError(null);
    summaryInFlightRef.current = false;
    autoSummaryInFlightRef.current = false;
    autoSummaryDeadlineRef.current = 0;
    summaryRequestTokenRef.current += 1;
  }, [clearLiveDeltas, stopStream]);

  useEffect(() => {
    return () => {
      stopStream();
    };
  }, [stopStream]);

  return {
    sessionId,
    liveDeltas,
    swarmState,
    events,
    status,
    starting,
    streaming,
    intervening,
    stopping,
    pauseUpdating,
    delayUpdating,
    summarizing,
    autoSummarizing,
    summaries,
    summary: summaries.length > 0 ? summaries[summaries.length - 1] : null,
    error,
    isActive: Boolean(sessionId),
    startSwarm,
    loadSession,
    intervene,
    stopSwarm,
    pauseSwarm,
    resumeSwarm,
    setPauseMode,
    setTurnDelay,
    getSummary,
    refreshStatus,
    resetSwarm,
    stopStream,
    participantDisplayNames,
    isTerminal: isTerminalState(swarmState),
    isHistoricalLoad: isHistoricalLoadRef.current,
  };
};
